import React, { memo, useMemo } from 'react';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import { DialogHeading } from 'components/dialog-title';
import { IUseForm, useFormHook, validateRule } from 'AurionCR/components/formV2';
import { IGridEditFormProps } from 'components/models';
import {
  API_CLINICAL_MEETING_ACTIVITIES,
  ClinicalMeetingActivity,
} from 'services/clinical-meeting-activities';
import { useRequestNotify, useSourcePatientRemarkStatuses } from 'components/hooks';
import { useTranslate } from 'hooks/use-translate';
import { SelectEmployee } from 'components/select-employee';
import { Controller } from 'react-hook-form';
import { useFieldProps } from 'hooks';
import { AppSelect } from 'components/app-select';
import { AppInput } from 'components/app-input';
import { ButtonCancel, ButtonSave } from '../app-buttons';

interface Props extends IGridEditFormProps {
  clinicalMeetingID: string;
  appUserID: string;
}
export const Editor = memo<Props>(
  ({ onClose, onFormHandle, itemID, clinicalMeetingID, appUserID }) => {
    const { t } = useTranslate();

    const config = useMemo<IUseForm<ClinicalMeetingActivity>>(() => {
      return {
        editID: itemID,
        fields: {
          ...new ClinicalMeetingActivity(),
          clinicalMeetingID: clinicalMeetingID,
          userEmployeeProfileID: appUserID,
          entryDate: new Date().toISOString(),
        },
        endpoint: API_CLINICAL_MEETING_ACTIVITIES.API_ENDPOINT,
        get: {
          parser: (data) => {
            const value = data?.value[0] || null;
            if (value && value.entryDate) value.entryDate = new Date(value.entryDate);
            return value;
          },
        },
        formHandle: onFormHandle,
      };
    }, [itemID, onFormHandle, clinicalMeetingID, appUserID]);

    const {
      formLoading,
      setFormLoading,
      formUse: {
        control,
        formState: { errors },
        handleSubmit,
      },
      onSubmit,
    } = useFormHook(config);

    const { requestWrap: wrapSubmit } = useRequestNotify({
      request: onSubmit,
      setLoading: setFormLoading,
    });

    const sourceRemarks = useSourcePatientRemarkStatuses();

    const getFieldProps = useFieldProps({ errors, emptyHelperText: '' });

    return (
      <Dialog open={true} onClose={onClose} fullWidth maxWidth="sm">
        <DialogHeading
          isLoading={formLoading}
          title={t('activity')}
          onClose={onClose}
          disabled={formLoading}
        />
        <DialogContent>
          <Grid container columnSpacing={2.5}>
            <Grid
              size={{
                md: 7,
              }}
            >
              <Controller
                name={'remarkForPatientCallStatusID'}
                control={control}
                rules={validateRule('required')}
                render={({ field: renderProps }) => (
                  <AppSelect
                    {...getFieldProps(renderProps)}
                    label={t('remarks-for-patient')}
                    options={sourceRemarks.data}
                    loading={sourceRemarks.loading}
                  />
                )}
              />
            </Grid>
            <Grid
              size={{
                md: 12,
              }}
            >
              <Controller
                name="userEmployeeProfileID"
                rules={validateRule('required')}
                control={control}
                render={({ field: renderProps }) => {
                  const props = getFieldProps(renderProps);
                  return <SelectEmployee {...props} label={t('employee')} disabled={formLoading} />;
                }}
              />
            </Grid>
            <Grid size={12}>
              <Controller
                name={'remarks'}
                control={control}
                render={({ field: renderProps }) => (
                  <AppInput {...getFieldProps(renderProps)} multiline minRows={5} />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <ButtonCancel isBack={true} onClick={onClose} />
          <ButtonSave isCreate={true} disabled={formLoading} onClick={handleSubmit(wrapSubmit)} />
        </DialogActions>
      </Dialog>
    );
  },
);
